import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCartCount } from '../../features/cart/cartSlice';
import { useNavigationTransition } from '../../hooks/useViewTransition';
import { useStorefrontCurrency } from '../../contexts/StorefrontCurrencyContext';
import ThemeToggle from '../storefront/ThemeToggle';

// Add responsive styles
const styles = `
  /* ── Floating pill header ──
     Desktop: contained max-width pill sitting 12px below the viewport
     top with rounded corners and a soft ambient shadow. Reads as a
     hovering card, not as a full-width bar.
     Mobile: flush-top, full-width, no rounded corners — visual weight
     belongs to the bottom dock on mobile, not the top bar. */
  .sf-header-shell {
    position: sticky;
    top: 0;
    z-index: 50;
    padding: 12px 24px 0;
    background: transparent;
    pointer-events: none; /* pill is the click surface, not the gutter */
  }
  .sf-header-pill {
    pointer-events: auto;
    max-width: 1200px;
    margin: 0 auto;
    background: var(--sf-bg, #ffffff);
    border: 1px solid var(--sf-border, #e5e7eb);
    border-radius: 18px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06);
  }
  @media (max-width: 768px) {
    .sf-header-shell {
      padding: 0;
    }
    .sf-header-pill {
      border-radius: 0;
      border-width: 0 0 1px 0;
      box-shadow: none;
    }
    .desktop-nav {
      display: none !important;
    }
    .mobile-menu-button {
      display: flex !important;
    }
    .categories-dropdown {
      display: none !important;
    }
    .store-name {
      font-size: 1.125rem !important;
    }
  }
  @media (max-width: 480px) {
    .store-name {
      font-size: 1rem !important;
    }
  }
  @media (min-width: 769px) {
    .desktop-nav {
      display: flex !important;
    }
    .mobile-menu-button {
      display: none !important;
    }
    .mobile-nav {
      display: none !important;
    }
  }

  /* Fix global button width conflicts for dropdowns */
  .sf-dropdown-item {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 100% !important;
    display: flex !important;
    box-sizing: border-box !important;
    justify-content: flex-start !important;
    text-align: left !important;
  }

  .sf-mobile-nav-item {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 100% !important;
    display: flex !important;
    box-sizing: border-box !important;
    justify-content: flex-start !important;
    text-align: left !important;
  }
`;

// Helper to check if we're on a subdomain
const isOnSubdomain = () => {
  const hostname = window.location.hostname;
  // Check if hostname has a subdomain (e.g., gthub-stores.localhost, not just localhost)
  return hostname.includes('.') && !hostname.startsWith('www.');
};

const Header = ({ theme, themeStyles, store, onOpenCart }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const cartCount = useSelector(selectCartCount);
  const { transitionTo, isNavigating } = useNavigationTransition();

  // Currency switching
  const {
    displayCurrency,
    setDisplayCurrency,
    getAvailableCurrencies,
    getCurrencyInfo
  } = useStorefrontCurrency();

  // Get products to extract categories
  const products = useSelector(state => state.store?.products || []);
  const categories = React.useMemo(() => {
    const cats = [...new Set(products.map(p => p.category).filter(Boolean))];
    return cats;
  }, [products]);

  // Safety check for store slug
  if (!store?.slug) {
    return null; // Don't render header if store is not loaded
  }

  // Check if we're on a subdomain
  const onSubdomain = isOnSubdomain();

  // Helper to generate the correct path based on subdomain vs path-based routing
  const getStorePath = (path = '') => {
    if (onSubdomain) {
      // On subdomain: use root-level paths
      return path ? `/${path}` : '/';
    } else {
      // Path-based: use /s/:slug/ format
      return path ? `/s/${store.slug}/${path}` : `/s/${store.slug}`;
    }
  };

  const showAboutPage = store?.settings?.storefrontDisplay?.showAboutPage !== false;
  const dynamicPages = (store?.pages || []).filter(p => p.showInNavigation !== false);
  const navigation = [
    { name: 'Home', href: '' },
    { name: 'Shop', href: 'shop' },
    ...(showAboutPage ? [{ name: 'About', href: 'about' }] : []),
    ...dynamicPages
      .filter(p => !['home','shop','about'].includes(p.slug))
      .map(p => ({ name: p.title, href: p.slug })),
  ];

  const isActive = (path) => {
    if (path === '') {
      if (onSubdomain) {
        return location.pathname === '/';
      }
      return location.pathname.endsWith('/s/' + store.slug) || location.pathname.endsWith('/s/' + store.slug + '/');
    }
    return location.pathname.includes('/' + path);
  };

  return (
    <>
      <style>{styles}</style>
      <div className="sf-header-shell">
      <header className="sf-header-pill">
      <div style={{
        padding: '0 1.5rem'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '4.5rem',
          gap: '2rem'
        }}>
          {/* Logo */}
          <Link
            to={getStorePath()}
            style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: themeStyles.primary,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              transition: 'transform 0.2s ease',
              letterSpacing: '-0.025em'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {store?.logo ? (
              /* When logo exists, only show the logo - no store name */
              <img
                src={store.logo}
                alt={store.name || 'Store'}
                style={{
                  height: '2.5rem',
                  width: 'auto',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                }}
              />
            ) : (
              /* When no logo, render just the monogram tile — same
                 single-mark treatment as the logo branch. Adding the
                 store name beside it pushed the pill off the right
                 edge on mobile (especially with long store names) and
                 duplicates the name that already appears in the hero
                 below. */
              <div
                style={{
                  width: '2.5rem',
                  height: '2.5rem',
                  backgroundColor: themeStyles.primary,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.125rem',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
                aria-label={store?.name || 'Store'}
              >
                {store?.name?.[0]?.toUpperCase() || 'S'}
              </div>
            )}
          </Link>

          {/* Desktop Navigation — centers within the flex-1 zone so the
              pill reads as 3-column (logo · nav · utilities) instead of
              everything bunching on the left. */}
          <nav className="desktop-nav" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            flex: 1
          }}>
            {navigation.map((item) => (
              <button
                key={item.name}
                onClick={async () => {
                  await transitionTo(() => {
                    navigate(getStorePath(item.href));
                  }, 'forward');
                }}
                disabled={isNavigating}
                className={`navigation-button ${isActive(item.href) ? 'is-active' : ''}`}
                style={{
                  color: isActive(item.href) ? themeStyles.primary : themeStyles.text,
                  textDecoration: 'none',
                  fontWeight: isActive(item.href) ? '600' : '500',
                  padding: '0.625rem 1rem',
                  background: isActive(item.href) ? `${themeStyles.primary}10` : 'transparent',
                  borderRadius: '10px',
                  transition: 'all 0.2s ease',
                  border: 'none',
                  cursor: isNavigating ? 'wait' : 'pointer',
                  fontSize: '15px',
                  fontFamily: 'inherit',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (!isActive(item.href)) {
                    e.currentTarget.style.backgroundColor = `${themeStyles.border}`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive(item.href)) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {item.name}
              </button>
            ))}
          </nav>

          {/* Categories Dropdown */}
          {categories.length > 0 && (
            <div className="categories-dropdown" style={{ position: 'relative' }}>
              <button
                className="sf-header-control"
                onClick={() => setIsCategoriesOpen(!isCategoriesOpen)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 1rem',
                  backgroundColor: isCategoriesOpen ? `${themeStyles.primary}15` : 'transparent',
                  border: `1.5px solid ${isCategoriesOpen ? themeStyles.primary : themeStyles.border}40`,
                  borderRadius: '10px',
                  color: isCategoriesOpen ? themeStyles.primary : themeStyles.text,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit',
                  fontSize: '15px',
                  fontWeight: '500',
                  boxShadow: isCategoriesOpen ? '0 4px 12px rgba(0, 0, 0, 0.08)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!isCategoriesOpen) {
                    e.currentTarget.style.backgroundColor = `${themeStyles.border}`;
                    e.currentTarget.style.borderColor = `${themeStyles.border}80`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCategoriesOpen) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = `${themeStyles.border}40`;
                  }
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7"/>
                  <rect x="14" y="3" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/>
                </svg>
                Categories
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{
                  transform: isCategoriesOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>

              {/* Dropdown Menu */}
              {isCategoriesOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 0.75rem)',
                    right: 0,
                    backgroundColor: themeStyles.background,
                    border: `1px solid ${themeStyles.border}20`,
                    borderRadius: '12px',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
                    width: '280px',
                    zIndex: 1000,
                    overflow: 'hidden',
                    backdropFilter: 'blur(10px)',
                    animation: 'slideDown 0.2s ease'
                  }}
                  onMouseLeave={() => setIsCategoriesOpen(false)}
                >
                  {categories.map((category, index) => (
                      <button
                        key={category}
                        className="sf-dropdown-item"
                        onClick={async () => {
                          setIsCategoriesOpen(false);
                          await transitionTo(() => {
                            navigate(getStorePath(`category/${encodeURIComponent(category)}`));
                          }, 'forward');
                        }}
                        disabled={isNavigating}
                      style={{
                        width: '100%',
                        padding: '0.875rem 1.25rem',
                        textAlign: 'left',
                        backgroundColor: themeStyles.background,
                        border: 'none',
                        color: themeStyles.text,
                        cursor: isNavigating ? 'wait' : 'pointer',
                        transition: 'all 0.15s ease',
                        fontSize: '15px',
                        fontFamily: 'inherit',
                        fontWeight: '500',
                        borderBottom: index < categories.length - 1 ? `1px solid ${themeStyles.border}20` : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '0.5rem',
                        boxSizing: 'border-box'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = `${themeStyles.primary}08`;
                        e.currentTarget.style.color = themeStyles.primary;
                        e.currentTarget.style.paddingLeft = '1.5rem';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = themeStyles.background;
                        e.currentTarget.style.color = themeStyles.text;
                        e.currentTarget.style.paddingLeft = '1.25rem';
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                      {category}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Cart & Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* Currency Switcher */}
            {getAvailableCurrencies().length > 1 && (
              <div style={{ position: 'relative' }}>
                <button
                  className="sf-header-control"
                  onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: isCurrencyOpen ? `${themeStyles.primary}10` : 'transparent',
                    border: `1.5px solid ${isCurrencyOpen ? themeStyles.primary : themeStyles.border}40`,
                    borderRadius: '8px',
                    color: isCurrencyOpen ? themeStyles.primary : themeStyles.text,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => {
                    if (!isCurrencyOpen) {
                      e.currentTarget.style.backgroundColor = `${themeStyles.border}`;
                      e.currentTarget.style.borderColor = `${themeStyles.border}80`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCurrencyOpen) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = `${themeStyles.border}40`;
                    }
                  }}
                >
                  <span style={{ fontWeight: '600' }}>{getCurrencyInfo(displayCurrency)?.symbol}</span>
                  <span>{displayCurrency}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{
                    transform: isCurrencyOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>

                {/* Currency Dropdown */}
                {isCurrencyOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 0.5rem)',
                      right: 0,
                      backgroundColor: themeStyles.background,
                      border: `1px solid ${themeStyles.border}20`,
                      borderRadius: '10px',
                      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                      minWidth: '140px',
                      zIndex: 1000,
                      overflow: 'hidden',
                      animation: 'slideDown 0.2s ease'
                    }}
                    onMouseLeave={() => setIsCurrencyOpen(false)}
                  >
                    {getAvailableCurrencies().map((curr, index) => (
                      <button
                        key={curr.code}
                        className="sf-dropdown-item"
                        onClick={() => {
                          setDisplayCurrency(curr.code);
                          setIsCurrencyOpen(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          textAlign: 'left',
                          backgroundColor: displayCurrency === curr.code ? `${themeStyles.primary}08` : themeStyles.background,
                          border: 'none',
                          color: displayCurrency === curr.code ? themeStyles.primary : themeStyles.text,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          fontWeight: displayCurrency === curr.code ? '600' : '500',
                          borderBottom: index < getAvailableCurrencies().length - 1 ? `1px solid ${themeStyles.border}15` : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                        onMouseEnter={(e) => {
                          if (displayCurrency !== curr.code) {
                            e.currentTarget.style.backgroundColor = `${themeStyles.primary}05`;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (displayCurrency !== curr.code) {
                            e.currentTarget.style.backgroundColor = themeStyles.background;
                          }
                        }}
                      >
                        <span style={{ fontWeight: '600', minWidth: '20px' }}>{curr.symbol}</span>
                        <span>{curr.code}</span>
                        {displayCurrency === curr.code && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 'auto' }}>
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Theme toggle (dark/light). Inherits color from header so
                no extra wiring is needed. */}
            <ThemeToggle style={{ color: themeStyles.text }} />

            {/* Cart */}
            <button
              className="sf-header-control sf-header-cart"
              onClick={() => onOpenCart && onOpenCart()}
              style={{
                position: 'relative',
                color: themeStyles.text,
                textDecoration: 'none',
                padding: '0.625rem',
                borderRadius: '10px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1.5px solid ${themeStyles.border}40`,
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontFamily: 'inherit'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${themeStyles.primary}10`;
                e.currentTarget.style.borderColor = `${themeStyles.primary}30`;
                e.currentTarget.style.color = themeStyles.primary;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = `${themeStyles.border}40`;
                e.currentTarget.style.color = themeStyles.text;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"/>
                <circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
              {cartCount > 0 && (
                <span className="sf-cart-count" style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  background: themeStyles.primary,
                  color: '#ffffff',
                  borderRadius: '999px',
                  minWidth: '18px',
                  height: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '0 5px',
                  // 2px page-bg ring "cuts" the badge out of the cart icon
                  // so it reads cleanly without a drop shadow or gradient.
                  border: `2px solid ${themeStyles.background}`,
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums'
                }}>
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="mobile-menu-button sf-header-control"
              style={{
                display: 'none',
                backgroundColor: isMenuOpen ? `${themeStyles.primary}10` : 'transparent',
                border: `1.5px solid ${isMenuOpen ? themeStyles.primary : themeStyles.border}40`,
                borderRadius: '10px',
                color: isMenuOpen ? themeStyles.primary : themeStyles.text,
                cursor: 'pointer',
                padding: '0.625rem',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!isMenuOpen) {
                  e.currentTarget.style.backgroundColor = `${themeStyles.border}`;
                  e.currentTarget.style.borderColor = `${themeStyles.border}80`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isMenuOpen) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = `${themeStyles.border}40`;
                }
              }}
            >
              {isMenuOpen ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <line x1="3" y1="12" x2="21" y2="12"/>
                  <line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="mobile-nav" style={{
            borderTop: `1px solid ${themeStyles.border}20`,
            padding: '1rem 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            animation: 'slideDown 0.2s ease'
          }}>
            {navigation.map((item) => (
              <button
                key={item.name}
                onClick={async () => {
                  setIsMenuOpen(false);
                  await transitionTo(() => {
                    navigate(getStorePath(item.href));
                  }, 'forward');
                }}
                disabled={isNavigating}
                className={`navigation-button sf-mobile-nav-item ${isActive(item.href) ? 'is-active' : ''}`}
                style={{
                  color: isActive(item.href) ? themeStyles.primary : themeStyles.text,
                  textDecoration: 'none',
                  fontWeight: isActive(item.href) ? '600' : '500',
                  padding: '0.875rem 1rem',
                  borderRadius: '10px',
                  backgroundColor: isActive(item.href) ? `${themeStyles.primary}10` : 'transparent',
                  borderLeft: isActive(item.href) ? `3px solid ${themeStyles.primary}` : '3px solid transparent',
                  transition: 'all 0.2s ease',
                  border: 'none',
                  cursor: isNavigating ? 'wait' : 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  fontSize: '15px',
                  fontFamily: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!isActive(item.href)) {
                    e.currentTarget.style.backgroundColor = `${themeStyles.border}`;
                    e.currentTarget.style.paddingLeft = '1.25rem';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive(item.href)) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.paddingLeft = '1rem';
                  }
                }}
              >
                {isActive(item.href) && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                )}
                {item.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </header>
    </div>
    </>
  );
};

export default Header;
